import { Link, useLocation } from "react-router-dom";
import { ShieldX, ChevronLeft } from "lucide-react";

import styles from "./ForbiddenPage.module.css";

interface Props {
  /**
   * Optional project context shown in the sunken card (mockup L212-L215).
   * If only `projectId` is known, it is rendered as `<code>`. If `projectName`
   * is supplied it is shown first.
   */
  projectName?: string;
  projectId?: string;
}

export function ForbiddenPage({ projectName, projectId }: Props = {}) {
  const location = useLocation();
  const isProjectRoute = location.pathname.startsWith("/projects/");
  const title = isProjectRoute
    ? "You don't have access to this project"
    : "You don't have access to this page";
  const body = isProjectRoute
    ? "This project exists, but you're not on its team. Ask a super admin or a team member to invite you."
    : "You're signed in, but this resource isn't available to your role. Ask a super admin if you think this is a mistake.";

  const hasContext = Boolean(projectName || projectId);

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={`${styles.icon} ${styles.iconWarn}`} aria-hidden="true">
          <ShieldX size={40} strokeWidth={2} />
        </div>
        <div className={styles.code}>Error 403 · admin_forbidden</div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.desc}>{body}</p>

        {hasContext && (
          <div className={styles.context}>
            <span className={styles.contextLabel}>Project</span>
            <span className={styles.contextValue}>
              {projectName && projectId ? (
                <>
                  {projectName} · <code>{projectId}</code>
                </>
              ) : projectName ? (
                projectName
              ) : (
                <code>{projectId}</code>
              )}
            </span>
          </div>
        )}

        <div className={styles.cta}>
          <Link to="/projects" className={`${styles.btn} ${styles.btnSecondary}`}>
            <ChevronLeft size={13} aria-hidden="true" />
            Back to my projects
          </Link>
          <Link to="/invites" className={`${styles.btn} ${styles.btnPrimary}`}>
            Check pending invites
          </Link>
        </div>

        <div className={styles.helper}>
          If you think this is a mistake — share this URL with a super admin:{" "}
          <code>{location.pathname}</code>
        </div>
      </div>
    </div>
  );
}
